import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type FavoriteSpotResponseDto,
  type CreateFavoriteSpotDto,
  type UpdateFavoriteSpotDto,
} from "@tabipla/shared";

export const favoriteKeys = {
  all: ["favorites"] as const,
  list: () => [...favoriteKeys.all, "list"] as const,
  detail: (id: string) => [...favoriteKeys.all, "detail", id] as const,
};

// Fetch all favorite spots for the current user
export function useFavorites() {
  return useQuery({
    queryKey: favoriteKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<FavoriteSpotResponseDto[]>("/api/favorites");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch favorites");
      }
      return response.data ?? [];
    },
  });
}

// Create a new favorite spot
export function useCreateFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateFavoriteSpotDto) => {
      const response = await apiClient.post<FavoriteSpotResponseDto>("/api/favorites", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create favorite");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteKeys.list() });
    },
  });
}

// Update a favorite spot
export function useUpdateFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateFavoriteSpotDto }) => {
      const response = await apiClient.put<FavoriteSpotResponseDto>(`/api/favorites/${id}`, input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update favorite");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteKeys.list() });
    },
  });
}

// Delete a favorite spot
export function useDeleteFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<null>(`/api/favorites/${id}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete favorite");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteKeys.list() });
    },
  });
}

// Helper to save a spot to favorites (from itinerary spot data)
export function useSaveToFavorites() {
  const createFavorite = useCreateFavorite();

  return useMutation({
    mutationFn: async (spot: {
      name: string;
      description?: string | null;
      category: "sightseeing" | "food" | "activity" | "transport" | "accommodation";
      location?: string | null;
      placeId?: string | null;
      latitude?: number | null;
      longitude?: number | null;
      formattedAddress?: string | null;
      cost?: number | null;
      notes?: string | null;
    }) => {
      return createFavorite.mutateAsync({
        name: spot.name,
        description: spot.description,
        category: spot.category,
        location: spot.location,
        placeId: spot.placeId,
        latitude: spot.latitude,
        longitude: spot.longitude,
        formattedAddress: spot.formattedAddress,
        estimatedCost: spot.cost,
        notes: spot.notes,
      });
    },
  });
}
